import type { Infer } from 'superstruct';
import {
	array,
	assign,
	enums,
	literal,
	nullable,
	number,
	object,
	omit,
	optional,
	partial,
	pick,
	record,
	string,
	union,
} from 'superstruct';

import {
	UploadGoogleAdsTemplateJobReport,
	ViewGoogleAdsAccountDto,
	ViewGoogleAdsTemplateDto,
} from '@adflow/shared/data-google-ads';
import { IdObjectWithName, stringDate } from '@adflow/shared/util-superstruct';
import { ViewUserDto } from '@adflow/shared/util-users';

import { historyArrayItem } from '../history-array-item';
import type { HistoryEntityType } from '../history-types';

import { HistoryGoogleAdsCampaignDto } from './history-google-ads-campaign.dto';

const HistoryGoogleAdsAccountDto = assign(
	IdObjectWithName,
	pick(ViewGoogleAdsAccountDto, ['avbId'])
);

export const HistoryGoogleAdsTemplateDto = assign(
	partial(
		omit(ViewGoogleAdsTemplateDto, [
			'updatedAt',
			'savedPromotionalAssets',
			'savedAudiences',
			'savedMerchantCenters',
			'savedGroupPromotionalAssets',
		])
	),
	partial(
		object({
			adAccounts: array(historyArrayItem(HistoryGoogleAdsAccountDto)),
			campaignTemplates: array(historyArrayItem(HistoryGoogleAdsCampaignDto)),
			savedPromotionalAssets: record(
				string(),
				array(historyArrayItem(number()))
			),
			savedGroupPromotionalAssets: record(
				string(),
				record(string(), array(historyArrayItem(number())))
			),
			savedAudiences: record(string(), string()),
			savedMerchantCenters: record(string(), string()),
		})
	)
);
export type HistoryGoogleAdsTemplateDto = Infer<
	typeof HistoryGoogleAdsTemplateDto
>;

const ViewHistoryGoogleAdsTemplateBaseDto = object({
	id: number(),
	entityId: number(),
	user: pick(ViewUserDto, ['id', 'name', 'email']),
	userId: number(),
	createdAt: stringDate(),
	origin: optional(enums(['USER', 'SALESFORCE'])),
});

export const ViewHistoryGoogleAdsTemplateUploadDto = assign(
	ViewHistoryGoogleAdsTemplateBaseDto,
	object({
		entityType: literal('GoogleAdsTemplate' satisfies HistoryEntityType),
		action: literal('UPLOAD'),
		oldData: optional(nullable(literal(null))),
		newData: UploadGoogleAdsTemplateJobReport,
	})
);
const ViewHistoryGoogleAdsTemplateOtherDto = assign(
	ViewHistoryGoogleAdsTemplateBaseDto,
	object({
		entityType: literal('GoogleAdsTemplate' satisfies HistoryEntityType),
		action: enums(['CREATE', 'UPDATE', 'DELETE']),
		oldData: nullable(optional(HistoryGoogleAdsTemplateDto)),
		newData: HistoryGoogleAdsTemplateDto,
	})
);

export type ViewHistoryGoogleAdsTemplateUploadDto = Infer<
	typeof ViewHistoryGoogleAdsTemplateUploadDto
>;

export const ViewHistoryGoogleAdsTemplateDto = union([
	ViewHistoryGoogleAdsTemplateUploadDto,
	ViewHistoryGoogleAdsTemplateOtherDto,
]);

export type ViewHistoryGoogleAdsTemplateDto = Infer<
	typeof ViewHistoryGoogleAdsTemplateDto
>;
